import React from 'react';
import { AspectRatio, MediaQuery, ResponsiveImageSpec, generateImageRenditions } from '../../utils/ImageUtils';
import { AImage } from '../../atoms/AImage';
import { AScrimGradient } from '../../atoms/AScrimGradient';
import { MButtonIcon, MButtonIconState } from '../MButtonIcon';
import { MLogoContainer } from '../MLogoContainer';
import { MTag } from '../MTag';
import { MMediaImageProps } from './MMediaImage.types';
import * as Styled from './MMediaImage.styles';

const MMEDIA_IMAGE_RENDITION_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.SQUARE, imgWidthPx: 540 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 360 },
  { media: MediaQuery.MIN_WIDTH_LG, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 585 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 750 },
];
const COMPONENT_NAME = 'm-web-ui-kit-MMediaImage';
const TEST_ID = 'web-ui-kit-MMediaImage';
const LOGO_ASPECT_RATIO = '3:2';
const LOGO_WIDTH = 72;
const LOGO_HEIGHT = (LOGO_WIDTH * 2) / 3;

export const MMediaImage: React.FC<MMediaImageProps> = ({
  aImage,
  mButtonIcon,
  aScrimGradient,
  mTag,
  mLogoContainer,
  item,
  className,
  renditionSpecs,
}) => {
  const renderedImage = aImage?.image || item?.aImage?.image;
  const tagLabel = typeof mTag === 'object' ? mTag?.aLabel : item?.mTag?.aLabel;
  const tagType = typeof mTag === 'object' ? mTag?.type : undefined;
  const specs = renditionSpecs ?? MMEDIA_IMAGE_RENDITION_SPECS;
  const rawButtonIcon = mButtonIcon || item?.mButtonIcon;
  const mButtonIconData = rawButtonIcon?.aIcon?.icon ? rawButtonIcon : undefined;
  const logoContainerData = mLogoContainer ?? item?.mLogoContainer;
  const logoBrand = logoContainerData?.aLogo32?.brand;
  const logoColor = logoContainerData?.aLogo32?.color ?? 'white';
  const logoPath = logoContainerData?.aLogo32?.logopath;
  const logoAlignment = logoContainerData?.aLogo32?.alignment;

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID}>
      {renderedImage && (
        <AImage
          image={renderedImage}
          renditions={generateImageRenditions(specs, renderedImage.assetPath)}
          loading="lazy"
        />
      )}
      {aScrimGradient && (
        <Styled.ScrimWrapper>
          <AScrimGradient direction={aScrimGradient.direction} />
        </Styled.ScrimWrapper>
      )}
      {mButtonIconData && (
        <Styled.ButtonIconWrapper>
          <MButtonIcon
            aIcon={mButtonIconData.aIcon}
            state={mButtonIconData.state ?? MButtonIconState.Transparent}
            onClick={mButtonIconData.onClick}
            aria-label={mButtonIconData['aria-label']}
          />
        </Styled.ButtonIconWrapper>
      )}
      {(logoBrand || logoPath) && (
        <Styled.LogoWrapper>
          <MLogoContainer
            brand={logoBrand ?? ''}
            aspectRatio={LOGO_ASPECT_RATIO}
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            color={logoColor}
            logopath={logoPath}
            alignment={logoAlignment}
          />
        </Styled.LogoWrapper>
      )}
      {(tagLabel?.text || typeof mTag === 'string') && (
        <Styled.TagWrapper>
          <MTag
            aLabel={{ text: tagLabel?.text || (typeof mTag === 'string' ? mTag : '') }}
            type={tagType}
            aIcon={typeof mTag === 'object' ? mTag?.aIcon : undefined}
          />
        </Styled.TagWrapper>
      )}
    </Styled.Wrapper>
  );
};
